import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DebtCard } from '../molecules/DebtCard';
import { SpendingChart } from '../molecules/SpendingChart';
import { ProgressBar } from '../atoms/ProgressBar';
import { AnimatedMetric } from '../atoms/AnimatedMetric';
import { theme } from '../../theme';
import { warBoard } from '../../language';
import { useMobile } from '../../hooks/useMediaQuery';

interface Debt {
  id: string;
  name: string;
  balance: number;
  originalBalance: number;
  interestRate: number;
  minimumPayment: number;
}

export const DebtWarfare = () => {
  const mobile = useMobile();
  const [debts] = useState<Debt[]>([
    { id: '1', name: 'Credit Card (Chase)', balance: 5847, originalBalance: 8000, interestRate: 18.9, minimumPayment: 150 },
    { id: '2', name: 'Personal Loan', balance: 12300, originalBalance: 15000, interestRate: 12.5, minimumPayment: 350 },
    { id: '3', name: 'Car Finance', balance: 8900, originalBalance: 12000, interestRate: 6.2, minimumPayment: 280 },
  ]);

  const [strategy, setStrategy] = useState<'avalanche' | 'snowball'>('avalanche');
  const [monthlyPayment, setMonthlyPayment] = useState(1000);

  const totalDebt = debts.reduce((s, d) => s + d.balance, 0);
  const totalOriginal = debts.reduce((s, d) => s + d.originalBalance, 0);
  const totalPaidOff = totalOriginal - totalDebt;
  const minimumPayments = debts.reduce((s, d) => s + d.minimumPayment, 0);
  const extraPayment = Math.max(0, monthlyPayment - minimumPayments);
  const freedomPct = Number(((totalPaidOff / totalOriginal) * 100).toFixed(1));

  const sortedDebts = [...debts].sort((a, b) =>
    strategy === 'avalanche' ? b.interestRate - a.interestRate : a.balance - b.balance
  );

  const monthlyData = [
    { name: 'Jan', spending: 3200, budget: 2800 },
    { name: 'Feb', spending: 2800, budget: 2800 },
    { name: 'Mar', spending: 3500, budget: 2800 },
    { name: 'Apr', spending: 2600, budget: 2800 },
    { name: 'May', spending: 2400, budget: 2800 },
    { name: 'Jun', spending: 2200, budget: 2800 },
  ];

  const categoryData = [
    { name: 'Housing', value: 1200 },
    { name: 'Food', value: 600 },
    { name: 'Transport', value: 400 },
    { name: 'Debt Payments', value: 1000 },
    { name: 'Entertainment', value: 300 },
    { name: 'Other', value: 200 },
  ];

  const pad = mobile ? theme.spacing[4] : theme.spacing[8];

  const SectionLabel = ({ children, color = theme.semantic.text.muted }: { children: string; color?: string }) => (
    <div style={{
      fontFamily: theme.typography.fontFamily.display,
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.bold,
      color,
      letterSpacing: theme.typography.letterSpacing.widest,
      textTransform: 'uppercase' as const,
      marginBottom: theme.spacing[4],
      paddingBottom: theme.spacing[2],
      borderBottom: `${theme.borders.medium} ${color}`,
      display: 'inline-block',
    }}>
      {children}
    </div>
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: pad }}>

      {/* Metrics row: 4 across on desktop, 2x2 on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: theme.spacing[0],
        marginBottom: theme.spacing[6],
        border: `${theme.borders.thin} ${theme.semantic.border.default}`,
      }}>
        <AnimatedMetric label={warBoard.topMetrics.totalDebt} value={totalDebt} prefix="£" variant="danger" />
        <AnimatedMetric label={warBoard.topMetrics.crushed} value={totalPaidOff} prefix="£" variant="success" />
        <AnimatedMetric label={warBoard.topMetrics.freedom} value={freedomPct} suffix="%" variant="warning" />
        <AnimatedMetric label={warBoard.topMetrics.monthlyPower} value={monthlyPayment} prefix="£" variant="neutral" />
      </div>

      {/* Master progress */}
      <div style={{
        background: theme.semantic.bg.surface,
        padding: mobile ? theme.spacing[4] : theme.spacing[5],
        marginBottom: mobile ? theme.spacing[6] : theme.spacing[8],
        borderTop: `${theme.borders.heavy} ${theme.colors.success}`,
      }}>
        <ProgressBar value={totalPaidOff} max={totalOriginal} label={warBoard.progress} color="success" height="lg" />
      </div>

      {/* Strategy + Targets: side-by-side on desktop, stacked on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobile ? '1fr' : '340px 1fr',
        gap: theme.spacing[6],
        marginBottom: mobile ? theme.spacing[6] : theme.spacing[8],
      }}>
        <div>
          {/* Strategy */}
          <div style={{
            background: theme.semantic.bg.surface,
            borderTop: `${theme.borders.heavy} ${theme.brand.copper}`,
            padding: mobile ? theme.spacing[4] : theme.spacing[5],
            marginBottom: theme.spacing[4],
          }}>
            <SectionLabel color={theme.brand.copper}>{warBoard.sections.strategy}</SectionLabel>
            <div style={{ display: 'flex', gap: 0 }}>
              {(['avalanche', 'snowball'] as const).map((s) => {
                const active = strategy === s;
                return (
                  <button
                    key={s}
                    onClick={() => setStrategy(s)}
                    style={{
                      flex: 1,
                      padding: `${theme.spacing[3]} ${theme.spacing[2]}`,
                      background: active ? theme.brand.copper : theme.semantic.bg.elevated,
                      border: 'none',
                      color: active ? theme.brand.cream : theme.semantic.text.muted,
                      fontFamily: theme.typography.fontFamily.display,
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.bold,
                      letterSpacing: theme.typography.letterSpacing.wider,
                      textTransform: 'uppercase' as const,
                      cursor: 'pointer',
                      minHeight: '44px',
                    }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <p style={{
              marginTop: theme.spacing[3],
              fontSize: theme.typography.fontSize.sm,
              color: theme.semantic.text.secondary,
              lineHeight: theme.typography.lineHeight.relaxed,
            }}>
              {warBoard.strategies[strategy].description}
            </p>
          </div>

          {/* Payment */}
          <div style={{
            background: theme.semantic.bg.surface,
            borderTop: `${theme.borders.heavy} ${theme.semantic.border.default}`,
            padding: mobile ? theme.spacing[4] : theme.spacing[5],
          }}>
            <SectionLabel>{warBoard.sections.warChest}</SectionLabel>
            <div style={{
              fontFamily: theme.typography.fontFamily.mono,
              fontSize: mobile ? theme.typography.fontSize['3xl'] : theme.typography.fontSize['4xl'],
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.semantic.text.primary,
              textAlign: 'center' as const,
              marginBottom: theme.spacing[4],
            }}>
              £{monthlyPayment.toLocaleString()}
            </div>
            <input
              type="range" min={minimumPayments} max={3000} step={50}
              value={monthlyPayment}
              onChange={(e) => setMonthlyPayment(Number(e.target.value))}
              style={{ width: '100%', accentColor: theme.brand.crimson, marginBottom: theme.spacing[4] }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              <div style={{ padding: theme.spacing[3], background: theme.semantic.bg.elevated, borderRight: `1px solid ${theme.semantic.border.default}` }}>
                <div style={{ fontFamily: theme.typography.fontFamily.display, fontSize: '0.65rem', color: theme.semantic.text.muted, letterSpacing: theme.typography.letterSpacing.widest, textTransform: 'uppercase' as const, marginBottom: theme.spacing[1] }}>{warBoard.slider.minimum}</div>
                <div style={{ fontFamily: theme.typography.fontFamily.mono, fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.brand.copper }}>£{minimumPayments}</div>
              </div>
              <div style={{ padding: theme.spacing[3], background: theme.semantic.bg.elevated }}>
                <div style={{ fontFamily: theme.typography.fontFamily.display, fontSize: '0.65rem', color: theme.semantic.text.muted, letterSpacing: theme.typography.letterSpacing.widest, textTransform: 'uppercase' as const, marginBottom: theme.spacing[1] }}>{warBoard.slider.extra}</div>
                <div style={{ fontFamily: theme.typography.fontFamily.mono, fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.success }}>+£{extraPayment}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Targets */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: theme.spacing[4] }}>
            <SectionLabel color={theme.brand.crimson}>{warBoard.sections.targets}</SectionLabel>
            <span style={{ fontFamily: theme.typography.fontFamily.sans, fontSize: theme.typography.fontSize.xs, color: theme.semantic.text.disabled }}>
              {strategy === 'avalanche' ? warBoard.sortLabels.interest : warBoard.sortLabels.balance}
            </span>
          </div>
          <AnimatePresence mode="popLayout">
            {sortedDebts.map((debt, i) => (
              <motion.div key={debt.id} layout initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ delay: i * 0.06 }}>
                <DebtCard {...debt} priority={i === 0 ? 'high' : i === 1 ? 'medium' : 'low'} onPaymentClick={() => console.log(`Attack ${debt.name}`)} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Charts: side-by-side on desktop, stacked on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
        gap: theme.spacing[4],
      }}>
        <SpendingChart data={monthlyData} type="line" title={warBoard.charts.spending} />
        <SpendingChart data={categoryData} type="pie" title={warBoard.charts.breakdown} />
      </div>
    </div>
  );
};

export default DebtWarfare;
